import { For, Show, createResource, createSignal } from 'solid-js';
import { useApi, isOk } from '../api';
import { firstError } from '../api/envelope';
import Text from '../components/Text';
import { EmptyState } from '../components/EmptyState';
import { SkeletonAuditRow } from '../components/Skeleton';
import { useToast } from '../components/MessageBox';
import { useTranslation } from '../hooks/useTranslation';
import { formatDateTime } from '../services/dateFormat';
import { formatInteger, formatPercent } from '../services/numberFormat';
import type { CaseWinStatsDto } from '../api/generated/schemas';
import styles from './AdminCaseOpeningStats.module.css';

const RANGES: { hours: number; labelKey: string }[] = [
    { hours: 24, labelKey: 'ADMIN_CASE_STATS_RANGE_DAY' },
    { hours: 168, labelKey: 'ADMIN_CASE_STATS_RANGE_WEEK' },
    { hours: 720, labelKey: 'ADMIN_CASE_STATS_RANGE_MONTH' },
    { hours: 0, labelKey: 'ADMIN_CASE_STATS_RANGE_ALL' },
];

export default function AdminCaseOpeningStats() {
    const api = useApi();
    const toast = useToast();
    const { t } = useTranslation();
    const [sinceHours, setSinceHours] = createSignal(0);

    const [stats] = createResource(sinceHours, async (hours): Promise<CaseWinStatsDto | null> => {
        try {
            const res = await api.caseOpeningAdmin.getCaseWinStats(hours > 0 ? { sinceHours: hours } : undefined);
            if (isOk(res.data)) return res.data.value;
            toast?.error(firstError(res.data) ?? t('ADMIN_CASE_STATS_LOAD_FAILED'));
        } catch (e: any) {
            toast?.error(e?.message ?? t('ADMIN_CASE_STATS_LOAD_FAILED'));
        }
        return null;
    });

    return (
        <div class="v" style={{ 'max-width': '1500px', margin: '0 auto', padding: 'var(--spacing-xl)', gap: 'var(--spacing-lg)' }}>
            <div class="v" style={{ gap: 'var(--spacing-xs)' }}>
                <h1 style={{ margin: 0 }}><Text val="ADMIN_CASE_STATS_TITLE" /></h1>
                <span class={styles.muted}><Text val="ADMIN_CASE_STATS_SUBTITLE" /></span>
            </div>

            <div class="fcard h" style={{ 'align-items': 'flex-end', gap: 'var(--spacing-md)', 'flex-wrap': 'wrap' }}>
                <label class="v" style={{ gap: 'var(--spacing-xs)' }}>
                    <span class={styles.muted}><Text val="ADMIN_CASE_STATS_RANGE" /></span>
                    <select value={sinceHours()} onInput={(e) => setSinceHours(parseInt(e.currentTarget.value, 10))}>
                        <For each={RANGES}>{(range) => <option value={range.hours}>{t(range.labelKey)}</option>}</For>
                    </select>
                </label>
                <Show when={stats()?.wins}>
                    <span class={styles.muted}>
                        {t('ADMIN_CASE_STATS_SUMMARY', formatInteger(stats()!.wins), formatInteger(stats()!.cases.length), formatInteger(stats()!.bots))}
                        <Show when={stats()!.firstWonAt && stats()!.lastWonAt}>
                            {' · '}{formatDateTime(stats()!.firstWonAt!)} - {formatDateTime(stats()!.lastWonAt!)}
                        </Show>
                    </span>
                </Show>
            </div>

            <Show when={stats.state !== 'pending'} fallback={
                <div class="v" style={{ gap: 'var(--spacing-sm)' }}>
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                    <SkeletonAuditRow />
                </div>
            }>
                <Show when={(stats()?.cases.length ?? 0) > 0} fallback={
                    <EmptyState icon="fa-box-open" title={t('ADMIN_CASE_STATS_EMPTY')} hint={t('ADMIN_CASE_STATS_EMPTY_HINT')} />
                }>
                    <div class={styles.caseGrid}>
                        <For each={stats()!.cases}>{(caseStats) =>
                            <div class="fcard v" style={{ gap: 'var(--spacing-sm)' }}>
                                <div class={styles.caseHeader}>
                                    <span class={styles.caseName}>{caseStats.label}</span>
                                    <span class={styles.muted}>
                                        {t('ADMIN_CASE_STATS_CASE_META', formatInteger(caseStats.wins), formatInteger(caseStats.bots))}
                                    </span>
                                </div>
                                <table class={styles.prizeTable}>
                                    <thead>
                                        <tr>
                                            <th><Text val="ADMIN_CASE_STATS_PRIZE" /></th>
                                            <th class={styles.number}><Text val="ADMIN_CASE_STATS_COUNT" /></th>
                                            <th class={styles.number}><Text val="ADMIN_CASE_STATS_SHARE" /></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <For each={caseStats.prizes}>{(prize) =>
                                            <tr>
                                                <td>{prize.prize}</td>
                                                <td class={styles.number}>{formatInteger(prize.wins)}</td>
                                                <td class={styles.number}>{formatPercent(prize.wins / caseStats.wins)}</td>
                                            </tr>
                                        }</For>
                                    </tbody>
                                </table>
                            </div>
                        }</For>
                    </div>
                </Show>
            </Show>
        </div>
    );
}
